import { jsx as _jsx } from "react/jsx-runtime";
import { getSession } from '#pplx/frontend/auth/core.js';
import { AuthError } from '#pplx/frontend/auth/errors.js';
import { SessionContext } from '#pplx/frontend/auth/SessionContext.js';
import { normalizeEnterpriseSession } from '#pplx/frontend/auth/subscriptionTier.js';
import { clearActiveAccountId, getActiveAccountId, } from '#pplx/frontend/browser-storage/activeAccount.js';
import { useLocalStorage } from '#pplx/frontend/browser-storage/hooks/useLocalStorage.js';
import { clearLastActiveAccountIfMatches } from '#pplx/frontend/browser-storage/lastActiveAccount.js';
import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
const SESSION_KEY = 'pplx-next-auth-session';
/**
 * Provides authentication session state to the component tree.
 *
 * Features:
 * - Persists session to localStorage for faster initial renders
 * - Automatically refetches session when tab becomes visible to detect logout in other tabs
 * - Optional periodic refetching to detect server-side session changes
 * - Deep equality check on session.user to prevent unnecessary re-renders
 */
export function SessionProvider({ children, preferInitialSession = false, refetchInterval, session: initialSession, }) {
    // Persist session to localStorage so subsequent page loads can display user state immediately
    // without waiting for the server roundtrip
    const [storedSession, setStoredSession] = useLocalStorage(SESSION_KEY, initialSession ?? null);
    const shouldUseInitialSession = useRef(preferInitialSession && initialSession !== undefined);
    const preserveSessionOnEmptyRefresh = useRef(false);
    const rawSession = shouldUseInitialSession.current ? (initialSession ?? null) : storedSession;
    const session = useMemo(() => normalizeEnterpriseSession(rawSession), [rawSession]);
    const setSession = useCallback((value) => {
        shouldUseInitialSession.current = false;
        setStoredSession(value);
    }, [setStoredSession]);
    // Initialize status based on localStorage value - if session exists, assume authenticated
    // to avoid flash of unauthenticated state on page load
    const [loading, setLoading] = useState(session ? false : true);
    /**
     * Updates the session without updating the loading state.
     * This is only used for auto-refreshing the session where
     * we don't necessarily wanna update the loading state since it'll flicker.
     */
    const backgroundUpdateSession = useCallback(async () => {
        let newSession = null;
        let getSessionError = null;
        try {
            const activeAccountId = getActiveAccountId();
            newSession = await getSession();
            if (newSession === null && activeAccountId) {
                if (getActiveAccountId() === activeAccountId) {
                    clearActiveAccountId();
                }
                clearLastActiveAccountIfMatches(activeAccountId);
                newSession = await getSession();
            }
            if (newSession?.user?.email) {
                preserveSessionOnEmptyRefresh.current = false;
            }
        }
        catch (error) {
            // the only option from getSession
            getSessionError = error;
        }
        // Use deep equality check to prevent unnecessary re-renders when session data hasn't changed.
        // The session object may be recreated on the server with the same data, so we compare
        // the actual user object rather than reference equality.
        setSession((oldSession) => {
            if (newSession === null && preserveSessionOnEmptyRefresh.current && oldSession?.user?.email) {
                newSession = oldSession;
                return oldSession;
            }
            if (isNetworkError(getSessionError) || areSessionsEquivalent(newSession, oldSession)) {
                // Return the old session object to maintain referential equality and prevent
                // downstream components from re-rendering
                newSession = oldSession;
                return oldSession;
            }
            return newSession;
        });
        return newSession;
    }, [setSession]);
    /**
     * Updates the session and the status.
     * We do status update here since it's an explicit user action.
     *
     * When a session is passed in directly (e.g., the Office add-in pane gets
     * the session from its sign-in dialog via Office.context.ui.messageParent
     * because the cookie is storage-partitioned away from the iframe), we
     * write it straight through and skip the cookie-bound getSession probe.
     * Passing null is an explicit clear and must not be preserved by the
     * Office add-in empty-refresh guard.
     */
    const updateSession = useCallback(async (override) => {
        setLoading(true);
        let newSession = null;
        try {
            if (override === null || isSessionLike(override)) {
                preserveSessionOnEmptyRefresh.current = override !== null;
                newSession = normalizeEnterpriseSession(override);
                setSession(newSession);
            }
            else {
                newSession = await backgroundUpdateSession();
            }
        }
        finally {
            // Always update status even if the session fetch fails, to avoid being stuck in 'loading'
            setLoading(false);
        }
        return newSession;
    }, [backgroundUpdateSession, setSession]);
    // Periodically refetch session to detect server-side changes (e.g., admin revoked access,
    // subscription expired, etc.) that wouldn't be caught by visibility change detection
    useEffect(() => {
        if (refetchInterval) {
            const interval = setInterval(backgroundUpdateSession, refetchInterval * 1e3);
            return () => clearInterval(interval);
        }
        return undefined;
    }, [refetchInterval, backgroundUpdateSession]);
    // Refetch session when user returns to the tab to detect logout in other tabs/windows.
    // This handles the case where a user logs out in one tab and switches back to another tab -
    // without this, the old tab would still show the user as logged in until the page refreshes
    // or the refetch interval triggers. Also refetch session when browser comes back online -
    // we may have retained a stale cache from a network failure earlier.
    // WHY useEffect: subscribing to browser DOM events (visibilitychange, online).
    useEffect(() => {
        // Both events share the visibility guard: if the tab is backgrounded when the device
        // reconnects, we intentionally skip the fetch — the visibilitychange listener will
        // pick it up when the user returns to the tab.
        function handler() {
            if (document.visibilityState === 'visible') {
                backgroundUpdateSession();
            }
        }
        document.addEventListener('visibilitychange', handler, false);
        window.addEventListener('online', handler, false);
        return () => {
            document.removeEventListener('visibilitychange', handler, false);
            window.removeEventListener('online', handler, false);
        };
    }, [backgroundUpdateSession]);
    // Refetch session when the component mounts to ensure the initial session is loaded.
    useEffect(() => {
        (async () => {
            try {
                await backgroundUpdateSession();
            }
            catch {
                // ignore
            }
            finally {
                setLoading(false);
            }
        })();
    }, [backgroundUpdateSession]);
    // Memoize context value to prevent unnecessary re-renders of all consuming components.
    const value = useMemo(() => session
        ? { session, status: 'authenticated', update: updateSession }
        : {
            session: null,
            status: loading ? 'loading' : 'unauthenticated',
            update: updateSession,
        }, [loading, session, updateSession]);
    return _jsx(SessionContext.Provider, { value: value, children: children });
}
function isNetworkError(error) {
    return error?.code === 'AUTH_NO_STATUS_CODE_ERROR';
}
function areSessionsEquivalent(newSession, oldSession) {
    return (isEqual(newSession?.user, oldSession?.user) &&
        newSession?.serverEnvironment === oldSession?.serverEnvironment);
}
function isSessionLike(value) {
    return typeof value?.user?.email === 'string';
}
//# sourceMappingURL=SessionProvider.js.map